import {InboundBridgeBucketRecord} from './index';
import ReactEcharts from 'echarts-for-react';
import React from 'react';
import {Box, Container, Header, Spinner} from '@amzn/awsui-components-react';
import {chartColorsLight} from '../common/colors';

const COMPONENT_TEXTS = {
    chart_description:
        'The Weighted Absolute Percentage Error (WAPE) contribution metrics have been normalized to align with the total WAPE. Top 10 biggest contributors are shown.',
} as const;

export function InboundBridgeStackedBarChart({data, isLoading}: {data: InboundBridgeBucketRecord[]; isLoading: boolean}) {
    const renderData = transformData(data);

    if (isLoading) {
        return (
            <Container
                header={
                    <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                        Daily WAPE Contribution
                    </Header>
                }
            >
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            </Container>
        );
    }

    return (
        <Container
            header={
                <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                    Daily WAPE Contribution
                </Header>
            }
        >
            <ReactEcharts
                key={JSON.stringify(renderData)}
                option={getOption()}
                style={{height: '50vh'}}
                opts={{renderer: 'svg'}}
                data-testid="page-stacked-bar-chart"
            />
        </Container>
    );

    function getOption() {
        const emSize = parseFloat(getComputedStyle(document.body).fontSize);

        return {
            color: chartColorsLight,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {
                type: 'scroll',
                itemWidth: emSize,
                itemHeight: emSize,
            },
            xAxis: {
                type: 'category',
                data: renderData.dates,
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value) => (value * 100).toFixed(2) + '%',
                },
            },
            series: getSeries(renderData),
        };
    }
}

export function getSeries(data: BucketData) {
    return data.buckets.map((bucket) => {
        return {
            name: bucket.name,
            type: 'bar',
            stack: 'total',
            label: {
                show: true,
                color: '#ffffff',
                formatter: (params) => (params.value * 100 < 1 ? '' : (params.value * 100).toFixed(2) + '%'),
            },
            emphasis: {
                disabled: true,
            },
            data: bucket.wapeContribsRescaled,
            itemStyle: {
                borderRadius: [4, 4, 4, 4],
            },
        };
    });
}

export function transformData(records: InboundBridgeBucketRecord[]): BucketData {
    records = records.filter((record: InboundBridgeBucketRecord) => record.wapeContribRescaled !== null);

    const dates = Object.keys(
        records.reduce((acc: Record<string, boolean>, record: InboundBridgeBucketRecord) => {
            acc[record.date] = true;
            return acc;
        }, {})
    ).sort();

    interface BucketGroup {
        seqId: number;
        name: string;
        values: Record<string, number>;
    }

    const topBucketsPerDate = dates.flatMap((date) =>
        records
            .filter((record) => record.date === date)
            .sort((a, b) => (b.wapeContribRescaled || 0) - (a.wapeContribRescaled || 0))
            .slice(0, 10) // TODO: Will make this actionable
            .sort((a, b) => a.seqId - b.seqId)
    );

    const bucketGroups = topBucketsPerDate.reduce((acc: Record<string, BucketGroup>, record: InboundBridgeBucketRecord) => {
        const key = `${record.seqId}-${record.name}`;
        if (!acc[key]) {
            acc[key] = {
                seqId: record.seqId,
                name: record.name,
                values: {},
            };
        }
        acc[key].values[record.date] = record.wapeContribRescaled || 0;
        return acc;
    }, {});

    const buckets = Object.keys(bucketGroups).map((key) => {
        const bucket = bucketGroups[key];
        return {
            seqId: bucket.seqId,
            name: bucket.name,
            wapeContribsRescaled: dates.map((date) => bucket.values[date] || 0),
        };
    });

    return {dates, buckets};
}

export type BucketData = {
    dates: string[];
    buckets: {
        seqId: number;
        name: string;
        wapeContribsRescaled: number[];
    }[];
};
